import React, { useState } from 'react';
import { Modal, Button, Spinner, Progress } from 'flowbite-react';
import AddQuestionsManual from './AddQuestionsManual';
import AIQuestionReviewDialog from './AIQuestionReviewDialog';
import { useAuth } from '../../utils/Auth/AuthProvider';
import { request } from '../../utils/remote';
import { GoogleGenAI } from '@google/genai';

interface AddOtherQuestionsDialogProps {
    show: boolean;
    onClose: () => void;
    examId: string;
    onQuestionsAdded: () => void;
}

const parseModelResponse = (r: any): string | null => {
    if (!r) return null
    if (typeof r === 'string') return r
    if (typeof r.text === 'string') return r.text
    if (Array.isArray(r.candidates) && r.candidates.length && typeof r.candidates[0].content === 'string') return r.candidates[0].content
    if (Array.isArray(r.outputs) && r.outputs.length) {
        const out0 = r.outputs[0]
        if (typeof out0 === 'string') return out0
        if (out0?.content && typeof out0.content === 'object') {
            if (typeof out0.content?.text === 'string') return out0.content.text
            if (Array.isArray(out0.content) && out0.content.length && typeof out0.content[0]?.text === 'string') return out0.content[0].text
        }
    }
    return null
}

const AddOtherQuestionsDialog: React.FC<AddOtherQuestionsDialogProps> = ({ show, onClose, examId, onQuestionsAdded }) => {
    const { token } = useAuth();
    const [mode, setMode] = useState<'choice' | 'manual' | 'ai'>('choice');
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractedData, setExtractedData] = useState<{questions: any[], shared_notes?: any[]}>({questions: []});
    const [showReview, setShowReview] = useState(false);
    const [isSavingManual, setIsSavingManual] = useState(false);
    
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [extractionProgress, setExtractionProgress] = useState<number>(0);
    const [extractionMessage, setExtractionMessage] = useState<string>('');

    const getUserKeyOrPrompt = async (): Promise<string | null> => {
        try {
            const kd = await request('/api/user/gemini-key/', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
            return kd?.has_key && kd?.api_key ? kd.api_key : null
        } catch (e) {
            console.warn('Could not fetch gemini key', e)
            return null
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const runNewAIExtraction = async () => {
        if (!selectedFile) {
            alert('Please select a PDF file first.');
            return;
        }

        const geminiKey = await getUserKeyOrPrompt();
        if (!geminiKey) {
            alert('Please set your Gemini API key in your profile first.');
            return;
        }

        setIsExtracting(true);
        setExtractionProgress(10);
        setExtractionMessage('Uploading PDF to Gemini...');

        try {
            const ai = new GoogleGenAI({ apiKey: geminiKey.trim() });

            const prompt = (
                "Please extract all multiple-choice questions from the attached document. " +
                "For each question, provide: " +
                "1. The question text. " +
                "2. Four choices (a, b, c, d). " +
                "3. The correct answer (determine it if not explicitly mentioned). " +
                "4. A clear explanation of why the answer is correct (generate this). " +
                "Don't include question numbers. " +
                "IMPORTANT: If there is a paragraph, passage, writing, or context that multiple questions depend on, identify it. " +
                "Format the final output as a STRICT JSON object with two keys: " +
                "1. 'questions': An array of objects with keys 'question', 'choice_a', 'choice_b', 'choice_c', 'choice_d', 'ans', 'explanation', and an optional 'note_idx' (integer, index of the shared note it belongs to). " +
                "2. 'shared_notes': An array of objects with keys 'title' and 'content' (the paragraph/passage text). " +
                "Respond with the JSON object only."
            );

            const fileBuffer = await selectedFile.arrayBuffer();
            const base64Data = btoa(
                new Uint8Array(fileBuffer)
                    .reduce((data, byte) => data + String.fromCharCode(byte), '')
            );

            const result = await ai.models.generateContent({model: 'gemini-2.5-flash', contents: [
                {
                    inlineData: {
                        data: base64Data,
                        mimeType: 'application/pdf'
                    }
                },
                { text: prompt }
            ]});

            setExtractionProgress(80);
            setExtractionMessage('Parsing response...');

            const responseText = parseModelResponse(result);

            let parsed: any = { questions: [] };
            try {
                const cleanedText = responseText!.replace(/```json\n?|```/g, '').trim();
                const start = cleanedText.indexOf('{');
                const end = cleanedText.lastIndexOf('}');
                
                if (start !== -1 && end !== -1) {
                    parsed = JSON.parse(cleanedText.substring(start, end + 1));
                } else {
                    parsed = JSON.parse(cleanedText);
                }
            } catch (e) {
                console.error('Failed to parse AI extraction response', responseText);
                try {
                    const m = responseText!.match(/(\{\s*.*\}\s*)/s);
                    if (m) {
                        parsed = JSON.parse(m[1]);
                    } else {
                        throw new Error('No JSON object found in response');
                    }
                } catch (e2) {
                    throw new Error('AI returned invalid JSON format.');
                }
            }

            setExtractionProgress(100);
            setExtractedData(parsed);
            setShowReview(true);

        } catch (error) {
            console.error('AI Extraction failed', error);
            alert('Failed to extract questions: ' + (error instanceof Error ? error.message : String(error)));
        } finally {
            setIsExtracting(false);
            setExtractionProgress(0);
            setExtractionMessage('');
        }
    };

    const handleManualAdd = async (q: any) => {
        setIsSavingManual(true);
        try {
            await request(`/api/school/exam-detail/${examId}/add-other-questions/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ questions: [q] })
            });
            onQuestionsAdded();
            alert('Question added successfully!');
        } catch (error) {
            console.error('Failed to add question manually', error);
            alert('Failed to add question');
        } finally {
            setIsSavingManual(false);
        }
    };

    const reset = () => {
        setMode('choice');
        setExtractedData({questions: []});
        setShowReview(false);
        setSelectedFile(null);
    };

    return (
        <>
            <Modal show={show && !showReview} onClose={onClose} size="4xl" className="z-[11000]" style={{ zIndex: 11000 }}> 
                <Modal.Header>Add Other Questions</Modal.Header>
                <Modal.Body>
                    {mode === 'choice' && (
                        <div className="flex flex-col items-center justify-center space-y-4 py-10">
                            <h3 className="text-xl font-medium">How would you like to add questions?</h3>
                            <div className="flex gap-4">
                                <Button onClick={() => setMode('manual')}>Add Manually</Button>
                                <Button color="indigo" onClick={() => setMode('ai')}>Add using AI (PDF)</Button>
                            </div>
                        </div>
                    )}

                    {mode === 'manual' && (
                        <div>
                            <Button color="gray" size="xs" onClick={() => setMode('choice')} className="mb-4">Back</Button>
                            <AddQuestionsManual onAdd={handleManualAdd} />
                            {isSavingManual && <div className="flex items-center justify-center mt-2"><Spinner size="sm" /> Saving...</div>}
                        </div>
                    )}

                    {mode === 'ai' && (
                        <div className="space-y-6 py-4">
                            <div className="flex items-center justify-between">
                                <Button color="gray" size="xs" onClick={() => setMode('choice')}>Back</Button>
                                <h4 className="text-lg font-semibold">AI Question Extraction</h4>
                                <div />
                            </div>
                            
                            <div className="flex flex-col items-center space-y-4 border-2 border-dashed rounded-lg p-10 bg-gray-50 dark:bg-gray-800">
                                <p className="text-gray-600 dark:text-gray-300">Choose a PDF file to extract questions from.</p>
                                <input 
                                    aria-label="Upload PDF"
                                    type="file" 
                                    accept=".pdf" 
                                    onChange={handleFileChange} 
                                    className="cursor-pointer block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400" 
                                    disabled={isExtracting}
                                />
                                {selectedFile && !isExtracting && (
                                    <Button color="indigo" onClick={runNewAIExtraction}>Extract Questions</Button>
                                )}
                            </div>

                            {isExtracting && (
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <div className="text-sm font-medium text-blue-700 dark:text-blue-300">{extractionMessage}</div>
                                        <div className="text-sm font-medium text-blue-700 dark:text-blue-300">{extractionProgress}%</div>
                                    </div>
                                    <Progress progress={extractionProgress} color="blue" />
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            <AIQuestionReviewDialog 
                show={showReview} 
                onClose={() => { setShowReview(false); onClose(); reset(); }} 
                questions={extractedData.questions} 
                sharedNotes={extractedData.shared_notes}
                examId={examId}
                onSaveComplete={() => {
                    setShowReview(false);
                    onClose();
                    reset();
                    onQuestionsAdded();
                }}
            />
        </>
    );
};

export default AddOtherQuestionsDialog;
